import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { OverflowMenu } from "@/components/ui/OverflowMenu";
import { useIsMobile } from "@/hooks/useBreakpoint";
import { selectActiveForce, selectIsOverBudget, selectTotalCost, useForceStore } from "@/store/forceStore";
import { useUiStore } from "@/store/uiStore";

interface HeaderProps {
	onOpenPrintView: () => void;
}

export function Header({ onOpenPrintView }: HeaderProps) {
	const force = useForceStore(selectActiveForce);
	const rosters = useForceStore((s) => s.rosters);
	const totalCost = useForceStore(selectTotalCost);
	const isOverBudget = useForceStore(selectIsOverBudget);
	const closeRoster = useForceStore((s) => s.closeRoster);
	const validationMode = useForceStore((s) => s.validationMode);
	const toggleValidation = useForceStore((s) => s.toggleValidationMode);
	const setForceName = useForceStore((s) => s.setForceName);
	const setMaxPoints = useForceStore((s) => s.setMaxPoints);

	const isMobile = useIsMobile();
	const openSidebar = useUiStore((s) => s.openSidebar);
	const isOverflowMenuOpen = useUiStore((s) => s.isOverflowMenuOpen);
	const setOverflowMenuOpen = useUiStore((s) => s.setOverflowMenuOpen);

	// Local state for name input so typing is always responsive
	const [nameInput, setNameInput] = useState(force?.name ?? "");

	// Sync local state when force changes (e.g. opening a different roster)
	useEffect(() => {
		if (force) setNameInput(force.name);
	}, [force?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	// Check if the current input duplicates another roster's name
	const isDuplicateName = useMemo(() => {
		if (!force) return false;
		const trimmed = nameInput.trim().toLowerCase();
		if (!trimmed) return false;
		return rosters.some(
			(r) => r.id !== force.id && r.name.trim().toLowerCase() === trimmed,
		);
	}, [rosters, nameInput, force]);

	if (!force) return null;

	function handleNameChange(value: string) {
		setNameInput(value);
	}

	function handleNameBlur() {
		const trimmed = nameInput.trim();
		if (!trimmed || isDuplicateName) {
			// Revert to the last valid name
			setNameInput(force!.name);
			return;
		}
		if (trimmed !== force!.name) {
			setForceName(trimmed);
		}
	}

	function handleNameKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Enter") {
			(e.target as HTMLInputElement).blur();
		}
		if (e.key === "Escape") {
			setNameInput(force!.name);
			(e.target as HTMLInputElement).blur();
		}
	}

	const budgetPercent =
		force.maxPoints > 0 ? Math.min(100, Math.round((totalCost / force.maxPoints) * 100)) : 0;

	/* ------------------------------------------------------------------ */
	/*  Mobile header layout                                               */
	/* ------------------------------------------------------------------ */
	if (isMobile) {
		return (
			<header className="bg-bg-secondary border-b border-border-default shrink-0">
				<div className="h-[var(--header-height)] flex items-center justify-between px-3">
					{/* Left: Back icon + logo */}
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={closeRoster}
							className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted hover:text-text-primary transition-colors cursor-pointer"
							title="Back to roster library"
						>
							<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<polyline points="15 18 9 12 15 6" />
							</svg>
						</button>
						<button
							type="button"
							onClick={closeRoster}
							className="text-base font-bold text-accent-primary tracking-tight cursor-pointer"
							title="Back to roster library"
						>
							SPECTRE OPS
						</button>
					</div>

					{/* Center-right: Total cost */}
					<div className="flex items-center gap-2">
						<div
							className={`font-mono text-base font-bold px-2 py-0.5 rounded-md transition-colors ${
								isOverBudget
									? "text-accent-danger bg-accent-danger/10"
									: "text-accent-secondary bg-accent-secondary/10"
							}`}
						>
							{totalCost}r
							{force.maxPoints > 0 && (
								<span className="text-xs text-text-muted ml-1">/ {force.maxPoints}r</span>
							)}
						</div>

						{/* Overflow menu trigger */}
						<div className="relative">
							<button
								type="button"
								onClick={() => setOverflowMenuOpen(!isOverflowMenuOpen)}
								className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted hover:text-text-primary transition-colors cursor-pointer"
								title="More options"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<circle cx="12" cy="5" r="1" />
									<circle cx="12" cy="12" r="1" />
									<circle cx="12" cy="19" r="1" />
								</svg>
							</button>

							<OverflowMenu
								isOpen={isOverflowMenuOpen}
								onClose={() => setOverflowMenuOpen(false)}
							>
								{/* Force name */}
								<div>
									<label className="block text-xs text-text-muted uppercase tracking-wider mb-1">
										Force Name
									</label>
									<input
										type="text"
										value={nameInput}
										onChange={(e) => handleNameChange(e.target.value)}
										onBlur={handleNameBlur}
										onKeyDown={handleNameKeyDown}
										className={`w-full bg-bg-tertiary text-text-primary border rounded-md px-3 py-2 text-sm outline-none transition-colors ${
											isDuplicateName
												? "border-accent-danger focus:border-accent-danger"
												: "border-border-default focus:border-accent-primary"
										}`}
										placeholder="Force name..."
									/>
									{isDuplicateName && (
										<p className="text-xs text-accent-danger mt-1">Name already in use</p>
									)}
								</div>

								{/* Budget */}
								<div>
									<label className="block text-xs text-text-muted uppercase tracking-wider mb-1">
										Budget
									</label>
									<div className="flex items-center gap-2">
										<input
											type="number"
											value={force.maxPoints}
											onChange={(e) => setMaxPoints(Number(e.target.value))}
											className="flex-1 bg-bg-tertiary text-text-primary border border-border-default rounded-md px-3 py-2 text-sm outline-none focus:border-accent-primary font-mono"
											min={0}
											step={100}
										/>
										<span className="text-xs text-text-muted">r</span>
									</div>
								</div>

								{/* Actions */}
								<div className="flex flex-col gap-2 pt-1 border-t border-border-default">
									<button
										type="button"
										onClick={() => {
											toggleValidation();
											setOverflowMenuOpen(false);
										}}
										className="w-full text-left text-sm text-text-secondary hover:text-text-primary px-2 py-2 rounded hover:bg-bg-tertiary transition-colors cursor-pointer"
									>
										Validation: {validationMode === "strict" ? "Strict" : "Relaxed"}
									</button>
									<button
										type="button"
										onClick={() => {
											onOpenPrintView();
											setOverflowMenuOpen(false);
										}}
										className="w-full text-left text-sm text-text-secondary hover:text-text-primary px-2 py-2 rounded hover:bg-bg-tertiary transition-colors cursor-pointer"
									>
										Print View
									</button>
								</div>
							</OverflowMenu>
						</div>

						{/* Hamburger / sidebar toggle */}
						<button
							type="button"
							onClick={openSidebar}
							className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted hover:text-text-primary transition-colors cursor-pointer"
							title="Open sidebar"
						>
							<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<line x1="3" y1="6" x2="21" y2="6" />
								<line x1="3" y1="12" x2="21" y2="12" />
								<line x1="3" y1="18" x2="21" y2="18" />
							</svg>
						</button>
					</div>
				</div>

				{/* Budget progress bar — integrated into header */}
				{force.maxPoints > 0 && (
					<div className="h-1 bg-bg-primary">
						<div
							className={`h-full transition-all duration-300 ${
								isOverBudget ? "bg-accent-danger" : "bg-accent-primary"
							}`}
							style={{ width: `${budgetPercent}%` }}
						/>
					</div>
				)}
			</header>
		);
	}

	/* ------------------------------------------------------------------ */
	/*  Desktop header layout (unchanged)                                  */
	/* ------------------------------------------------------------------ */
	return (
		<header className="bg-bg-secondary border-b border-border-default shrink-0">
			<div className="h-[var(--header-height)] flex items-center justify-between px-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" size="sm" onClick={closeRoster} title="Back to roster library">
						&larr; Rosters
					</Button>
					<button
						type="button"
						onClick={closeRoster}
						className="text-lg font-bold text-accent-primary tracking-tight hover:text-accent-primary/80 transition-colors cursor-pointer"
						title="Back to roster library"
					>
						SPECTRE OPS
					</button>
					<span className="text-text-muted text-sm">Force Builder</span>
				</div>

				<div className="flex items-center gap-4">
					{/* Force name — editable with uniqueness validation */}
					<div className="relative">
						<input
							type="text"
							value={nameInput}
							onChange={(e) => handleNameChange(e.target.value)}
							onBlur={handleNameBlur}
							onKeyDown={handleNameKeyDown}
							className={`bg-bg-tertiary text-text-primary border rounded-md px-3 py-1 text-sm w-48 outline-none transition-colors ${
								isDuplicateName
									? "border-accent-danger focus:border-accent-danger"
									: "border-border-default focus:border-accent-primary"
							}`}
							placeholder="Force name..."
						/>
						{isDuplicateName && (
							<div className="absolute top-full left-0 mt-1 text-xs text-accent-danger whitespace-nowrap z-10">
								Name already in use
							</div>
						)}
					</div>

					{/* Max points */}
					<label className="flex items-center gap-2">
						<span className="text-xs text-text-muted uppercase tracking-wider">Budget</span>
						<input
							type="number"
							value={force.maxPoints}
							onChange={(e) => setMaxPoints(Number(e.target.value))}
							className="bg-bg-tertiary text-text-primary border border-border-default rounded-md px-2 py-1 text-sm w-20 text-right outline-none focus:border-accent-primary font-mono"
							min={0}
							step={100}
						/>
						<span className="text-xs text-text-muted">r</span>
					</label>

					{/* Total cost */}
					<div
						className={`font-mono text-lg font-bold px-3 py-1 rounded-md transition-colors ${
							isOverBudget
								? "text-accent-danger bg-accent-danger/10"
								: "text-accent-secondary bg-accent-secondary/10"
						}`}
					>
						{totalCost}r
						{force.maxPoints > 0 && (
							<span className="text-sm text-text-muted ml-1">/ {force.maxPoints}r</span>
						)}
					</div>

					{/* Validation mode toggle */}
					<Button
						variant="ghost"
						size="sm"
						onClick={toggleValidation}
						title={`Validation: ${validationMode}`}
					>
						{validationMode === "strict" ? "Strict" : "Relaxed"}
					</Button>

					{/* Print View button */}
					<Button
						variant="ghost"
						size="sm"
						onClick={onOpenPrintView}
						title="Open print-friendly stat card view"
					>
						Print View
					</Button>
				</div>
			</div>

			{/* Budget progress bar */}
			{force.maxPoints > 0 && (
				<div className="h-1 bg-bg-primary">
					<div
						className={`h-full transition-all duration-300 ${
							isOverBudget ? "bg-accent-danger" : "bg-accent-primary"
						}`}
						style={{ width: `${budgetPercent}%` }}
					/>
				</div>
			)}
		</header>
	);
}
